import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSEO from "@/hooks/useSEO";

const features = [
  { icon: "ri-flashlight-line", titleKey: "system.feature1.title", descKey: "system.feature1.desc", color: "cyan" },
  { icon: "ri-brain-line", titleKey: "system.feature2.title", descKey: "system.feature2.desc", color: "violet" },
  { icon: "ri-shield-keyhole-line", titleKey: "system.feature3.title", descKey: "system.feature3.desc", color: "emerald" },
  { icon: "ri-dashboard-3-line", titleKey: "system.feature4.title", descKey: "system.feature4.desc", color: "amber" },
  { icon: "ri-links-line", titleKey: "system.feature5.title", descKey: "system.feature5.desc", color: "rose" },
  { icon: "ri-file-chart-line", titleKey: "system.feature6.title", descKey: "system.feature6.desc", color: "sky" },
];

const techStack = [
  { name: "Cloud Infrastructure", icon: "ri-cloud-line", desc: "AWS / GCP" },
  { name: "Security", icon: "ri-shield-check-line", desc: "ISO 27001準拠" },
  { name: "API", icon: "ri-code-s-slash-line", desc: "RESTful / GraphQL" },
  { name: "Database", icon: "ri-database-2-line", desc: "PostgreSQL / Redis" },
  { name: "AI/ML", icon: "ri-robot-line", desc: "Machine Learning" },
  { name: "Monitoring", icon: "ri-pulse-line", desc: "24/7 Real-time" },
];

const AnimatedSection = ({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {children}
    </div>
  );
};

const colorMap: Record<string, { bg: string; text: string; border: string }> = {
  cyan: { bg: "bg-cyan-50", text: "text-cyan-600", border: "border-cyan-200" },
  violet: { bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-200" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
  amber: { bg: "bg-amber-50", text: "text-amber-600", border: "border-amber-200" },
  rose: { bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200" },
  sky: { bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-200" },
};

const SystemPage = () => {
  const { t } = useTranslation();

  useSEO({
    title: "システムについて | 決済管理自動化プラットフォーム",
    description: "Stream Solutions Limitedの自社開発システム。リアルタイムデータ連携・AI自動照合エンジン・セキュアクラウド基盤・カスタマイズダッシュボードで決済管理業務を完全自動化します。",
    keywords: "決済管理システム,AI照合エンジン,自動消込,リアルタイム連携,フィンテックシステム,クラウド決済",
    canonical: "/system",
    ogTitle: "システムについて | Stream Solutions Limited",
    ogDescription: "データ連携・自動化・可視化を一体的に実現する自社開発プラットフォーム。金融グレードのセキュリティで安心の運用を。",
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-950 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=futuristic-technology-system-architecture-dark-background-with-glowing-network-nodes-data-flow-visualization-circuit-board-patterns-cyan-blue-accent-lights-deep-space-atmosphere-no-people-ultra-modern-fintech&width=1920&height=600&seq=syshero001&orientation=landscape"
            alt="system hero"
            className="w-full h-full object-cover object-center opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 to-slate-950" />
        </div>
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "linear-gradient(rgba(6,182,212,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.3) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="w-8 h-px bg-cyan-500" />
            <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">SYSTEM</span>
            <span className="w-8 h-px bg-cyan-500" />
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">{t("system.hero.title")}</h1>
          <p className="text-slate-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("system.hero.sub")}
          </p>
        </div>
      </section>

      {/* Dashboard showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="relative rounded-3xl overflow-hidden bg-slate-900 p-1 shadow-2xl">
              <div className="flex items-center gap-2 px-5 py-3 bg-slate-800 rounded-t-2xl">
                <div className="w-3 h-3 rounded-full bg-red-400/60" />
                <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                <div className="w-3 h-3 rounded-full bg-green-400/60" />
                <div className="flex-1 mx-6 h-6 rounded-full bg-slate-700 flex items-center px-4">
                  <i className="ri-lock-line text-slate-500 text-xs mr-2" />
                  <span className="text-slate-500 text-xs">stream-solutions.app/dashboard</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-cyan-400 text-xs">LIVE</span>
                </div>
              </div>
              <img
                src="https://readdy.ai/api/search-image?query=advanced-financial-analytics-dashboard-dark-theme-with-multiple-charts-real-time-data-visualization-payment-reconciliation-system-cyan-accent-colors-professional-fintech-software-interface-clean-modern-UI-design&width=1400&height=700&seq=sysdash001&orientation=landscape"
                alt="System dashboard"
                className="w-full rounded-b-2xl object-cover object-top"
              />
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Features grid */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-8 h-px bg-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">FEATURES</span>
                <span className="w-8 h-px bg-cyan-500" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">主要機能</h2>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => {
              const c = colorMap[feat.color];
              return (
                <AnimatedSection key={feat.titleKey} delay={i * 0.08}>
                  <div className="group p-7 rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/30 hover:bg-slate-800/80 transition-all duration-500 h-full">
                    <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${c.bg} mb-5`}>
                      <i className={`${feat.icon} ${c.text} text-xl`} />
                    </div>
                    <h3 className="text-white font-semibold text-base mb-3 group-hover:text-cyan-300 transition-colors duration-300">
                      {t(feat.titleKey)}
                    </h3>
                    <p className="text-slate-400 text-sm leading-relaxed">{t(feat.descKey)}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Architecture diagram */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-8 h-px bg-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">ARCHITECTURE</span>
                <span className="w-8 h-px bg-cyan-500" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">システムアーキテクチャ</h2>
              <p className="text-slate-600 text-base max-w-xl mx-auto">
                スケーラブルで高可用性なマイクロサービスアーキテクチャを採用しています。
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <div className="relative rounded-3xl overflow-hidden bg-slate-950 p-8 lg:p-12">
              {/* Architecture visual */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Input layer */}
                <div className="space-y-4">
                  <div className="text-center text-xs font-semibold text-slate-400 tracking-widest uppercase mb-6">
                    Input Layer
                  </div>
                  {["銀行API", "外部システム", "手動入力"].map((item) => (
                    <div key={item} className="p-4 rounded-xl border border-slate-700 bg-slate-800/50 text-center">
                      <span className="text-slate-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Processing layer */}
                <div className="space-y-4">
                  <div className="text-center text-xs font-semibold text-cyan-400 tracking-widest uppercase mb-6">
                    Processing Layer
                  </div>
                  {["AI照合エンジン", "自動消込処理", "データ変換・統合", "リアルタイム分析"].map((item) => (
                    <div key={item} className="p-4 rounded-xl border border-cyan-500/30 bg-cyan-500/5 text-center">
                      <span className="text-cyan-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Output layer */}
                <div className="space-y-4">
                  <div className="text-center text-xs font-semibold text-slate-400 tracking-widest uppercase mb-6">
                    Output Layer
                  </div>
                  {["ダッシュボード", "自動レポート", "外部連携出力"].map((item) => (
                    <div key={item} className="p-4 rounded-xl border border-slate-700 bg-slate-800/50 text-center">
                      <span className="text-slate-300 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Arrows */}
              <div className="hidden lg:flex absolute top-1/2 left-[33%] -translate-y-1/2 items-center">
                <i className="ri-arrow-right-line text-cyan-500 text-xl" />
              </div>
              <div className="hidden lg:flex absolute top-1/2 left-[66%] -translate-y-1/2 items-center">
                <i className="ri-arrow-right-line text-cyan-500 text-xl" />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Tech stack */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="w-8 h-px bg-cyan-500" />
                <span className="text-cyan-400 text-xs font-semibold tracking-[0.3em] uppercase">TECHNOLOGY</span>
                <span className="w-8 h-px bg-cyan-500" />
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t("system.tech.title")}</h2>
              <p className="text-slate-400 text-base max-w-xl mx-auto">{t("system.tech.sub")}</p>
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 0.07}>
                <div className="group p-5 rounded-2xl border border-slate-800 bg-slate-900/60 hover:border-cyan-500/40 transition-all duration-300 text-center cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800 mx-auto mb-3 group-hover:bg-cyan-500/10 transition-colors duration-300">
                    <i className={`${tech.icon} text-slate-400 group-hover:text-cyan-400 text-lg transition-colors duration-300`} />
                  </div>
                  <div className="text-white text-xs font-semibold mb-1">{tech.name}</div>
                  <div className="text-slate-500 text-xs">{tech.desc}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <AnimatedSection>
            <div className="relative rounded-3xl overflow-hidden bg-slate-950 px-10 lg:px-16 py-16 text-center">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=abstract-technology-background-dark-with-glowing-cyan-network-nodes-data-streams-futuristic-digital-atmosphere-no-people-clean-minimal&width=1400&height=500&seq=syscta001&orientation=landscape"
                  alt="cta bg"
                  className="w-full h-full object-cover opacity-15"
                />
                <div className="absolute inset-0 bg-slate-950/80" />
              </div>
              <div className="relative z-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">{t("system.cta.title")}</h2>
                <p className="text-slate-400 text-base max-w-xl mx-auto mb-8">{t("system.cta.sub")}</p>
                <Link
                  to="/demo"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-400 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {t("system.cta.btn")}
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SystemPage;
